import { Middleware } from "@reduxjs/toolkit";
import { CALL_HISTORY_METHOD } from "connected-react-router";
import {
  addLanguageToPath,
  saveLanguagePreference,
  extractLanguageFromPath,
} from "@Utils/languageCore";

/**
 * Redux middleware that adds language prefix to router navigation
 *
 * Language Priority:
 * 1. Destination path prefix — if the target already has /fr/, keep it
 * 2. Current URL prefix — if the user is on /fr/..., keep navigating in French
 * 3. Default "en" — unprefixed current URL stays unprefixed
 *
 * Do NOT use the language cookie or a stale Redux userLanguage when the current
 * URL has no prefix. That mismatch is handled by LanguageRedirectGuard's modal.
 * Using either here re-prefixes /fr after a gesture-back to an English URL.
 */
const languageRouterMiddleware: Middleware = (store) => (next) => (action: any) => {
  if (action.type === CALL_HISTORY_METHOD) {
    const { payload } = action;
    const state = store.getState();

    if (payload?.method === "push" || payload?.method === "replace") {
      const [path, ...rest] = payload.args;

      // Same on `.cn`: extractLanguageFromPath only returns prefixed locales (never en / zh),
      // so /zhtw/ and /zhhk/ carry over exactly like /fr/.
      if (typeof path === "string") {
        const pathLanguage = extractLanguageFromPath(path);
        if (pathLanguage) {
          saveLanguagePreference(pathLanguage);
          return next(action);
        }

        const currentPathname = state.router?.location?.pathname || "";
        const currentUrlLanguage = extractLanguageFromPath(currentPathname);
        if (currentUrlLanguage && currentUrlLanguage !== "en") {
          const enhancedPath = addLanguageToPath(path, currentUrlLanguage);
          return next({
            ...action,
            payload: {
              ...payload,
              args: [enhancedPath, ...rest],
            },
          });
        }

        return next(action);
      }
    }
  }

  return next(action);
};

export default languageRouterMiddleware;
